import { useState, useEffect } from 'react'
import { Spin, Drawer } from 'antd'
import { Link } from 'react-router-dom'
import { CloseOutlined, FilterOutlined } from '@ant-design/icons'

import { useFilters, FilterKeyEnum } from 'src/shared/hooks'

import { PageInfo } from 'src/interfaces/backend'
import { Brand } from 'src/interfaces/ecommerce'

import { useListFilters } from 'src/api/graphql'

import BrandCarousel from '../components/BrandCarousel/BrandCarousel'
import { Filters } from 'src/shared/components'
import { FilterCategory } from 'src/shared/components/Filters'

import { ListProducts } from './List'

import { FilterButton, StoreStyled } from './styles'

interface OutputNode {
  key: string
  title: string
  children?: OutputNode[]
}

interface Subcategory {
  subcategory: string
  group?: string
}

interface Category {
  category: string
  subcategories: Subcategory[]
}

interface Item {
  brand: string
  categories: Category[]
}

const initialValues: Partial<PageInfo> = {
  currentPage: 1,
  perPage: 20,
}

const Products = () => {
  const [open, setOpen] = useState<boolean>(false)

  const [categories, setCategories] = useState<FilterCategory[]>([])
  const [brands, setBrands] = useState<Brand[]>([])
  const [pageInfo, setPageInfo] = useState<Partial<PageInfo>>(initialValues)

  const { getQueryParams } = useFilters()
  const queryParams = getQueryParams([FilterKeyEnum.q])
  const { q } = queryParams

  const queryFilters = useListFilters({ brands: [], categories: [], subcategories: [] })

  function groupByCategory(data: Item[]): OutputNode[] {
    const map = new Map<string, Map<string, Set<string>>>()

    // Agrupar subcategorías por categoría
    data.forEach((item) => {
      item.categories.forEach((cat) => {
        const catName = cat.category
        if (!map.has(catName)) {
          map.set(catName, new Map())
        }

        cat.subcategories.forEach((sub) => {
          const subName = sub.subcategory
          const groupName = sub.group

          if (!map.get(catName)!.has(subName)) {
            map.get(catName)!.set(subName, new Set())
          }

          if (groupName) {
            map.get(catName)!.get(subName)!.add(groupName)
          }
        })
      })
    })

    // Convertir y ordenar
    const result: OutputNode[] = []

    map.forEach((subMap, category) => {
      const subcategories: OutputNode[] = []

      subMap.forEach((groupSet, subcategory) => {
        const groupChildren: OutputNode[] = Array.from(groupSet).map((group) => ({
          key: group,
          title: group,
        }))

        subcategories.push({
          key: subcategory,
          title: subcategory,
          children: groupChildren.length > 0 ? groupChildren : undefined,
        })
      })

      result.push({
        key: category,
        title: category,
        children: subcategories,
      })
    })

    return result.sort((a, b) => a.title.localeCompare(b.title))
  }

  useEffect(() => {
    if (!queryFilters.loading && queryFilters.data) {
      const filters = queryFilters.data.filters ?? []

      const mapBrands = filters?.map((brand: any) => ({
        name: brand.brand,
        categories: brand.categories.map((cat: any) => cat.category),
      }))
      setBrands(mapBrands)

      const adaptedCategories = groupByCategory(filters)
      setCategories(adaptedCategories)
    }
  }, [queryFilters.loading, queryFilters.data])

  return (
    <StoreStyled>
      <BrandCarousel />
      <div className="filter-products-wrapper">
        <div className="dynamic-filters">
          <Spin spinning={queryFilters.loading || queryFilters.loading}>
            <Filters
              hasSearch={Boolean(q)}
              path="/products"
              categories={categories}
              brands={brands}
              onFiltered={() => setPageInfo((prev) => ({ ...prev, currentPage: 1 }))}
            />
          </Spin>
        </div>

        <ListProducts q={q} pageInfo={pageInfo} setPageInfo={setPageInfo} />
      </div>

      <Drawer
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Link className="logo-desktop" to="/">
              <img src="/logos/logo.svg" height="48" alt="Equip" />
            </Link>
            <CloseOutlined style={{ fontSize: 26 }} onClick={() => setOpen(false)} />
          </div>
        }
        placement="left"
        closable={false}
        onClose={() => setOpen(false)}
        open={open}
        key="left"
      >
        <Spin spinning={queryFilters.loading || queryFilters.loading}>
          <Filters
            path="/products"
            categories={categories}
            brands={brands}
            onFiltered={() => setPageInfo((prev) => ({ ...prev, currentPage: 1 }))}
          />
        </Spin>
      </Drawer>
    </StoreStyled>
  )
}

export default Products
